import {setCookie} from '@miro-site/core'
import React, {useEffect, useState} from 'react'
import {DEFAULT_LOCALE, LOCALE_COOKIE, LOCALE_LABELS, RTL_LANGUAGES} from '../constants'
import {LocalizationData, Translation} from '../types'
import toBaseLocale from '../utils/toBaseLocale'

export type LocalizationProviderProps = {
  children: React.ReactNode
  useBaseLocale?: string
  detectLocale?: boolean
  detectTranslations?: boolean
  changeLocaleOnLoad?: boolean
  data?: Partial<LocalizationData>
}

const defaultLocalizationData: LocalizationData = {
  defaultLocale: DEFAULT_LOCALE,
  currentLocale: DEFAULT_LOCALE,
  isRTL: RTL_LANGUAGES.includes(DEFAULT_LOCALE),
  localeLabels: LOCALE_LABELS,
  onChangeLocale: locale => setCookie(LOCALE_COOKIE, locale, 0),
  translations: [],
}

export const LocaleContext = React.createContext<LocalizationData>(defaultLocalizationData)

const LocalizationProvider = (props: LocalizationProviderProps): React.ReactElement => {
  const {
    children,
    data = {},
    detectLocale = true,
    detectTranslations = true,
    changeLocaleOnLoad = true,
    useBaseLocale = true,
  } = props

  const [detectedLocalizationData, setDetectedLocalizationData] = useState<
    Pick<LocalizationData, 'currentLocale' | 'translations'>
  >({
    currentLocale: DEFAULT_LOCALE,
    translations: [],
  })

  const mergedData = {
    ...defaultLocalizationData,
    ...detectedLocalizationData,
    ...data,
  }

  const normalizeLocale = (locale: string) => (useBaseLocale ? toBaseLocale(locale) : locale)

  const defaultLocale = normalizeLocale(mergedData.defaultLocale)
  const currentLocale = normalizeLocale(mergedData.currentLocale)
  const translations = (mergedData.translations || []).map(t => ({
    ...t,
    locale: normalizeLocale(t.locale),
  }))

  useEffect(() => {
    let detectedLocale: string = defaultLocale
    let detectedTranslations: Translation[] = []

    if (detectLocale) {
      const pathLocale = document.location.pathname.split('/')[1].toLowerCase()
      detectedLocale = pathLocale && LOCALE_LABELS[pathLocale] ? pathLocale : defaultLocale
    }

    if (detectTranslations) {
      detectedTranslations = Object.keys(window.mrTranslations || {}).reduce((acc, locale) => {
        const a = document.createElement('a')
        a.href = window.mrTranslations[locale]

        acc.push({
          locale: locale,
          url: a.pathname || '/',
        })

        return acc
      }, [] as Translation[])
    }

    if (detectLocale || detectTranslations) {
      setDetectedLocalizationData({
        currentLocale: detectedLocale,
        translations: detectedTranslations,
      })
    }
  }, [detectLocale, detectTranslations])

  useEffect(() => {
    if (changeLocaleOnLoad && currentLocale !== defaultLocale) {
      mergedData.onChangeLocale(currentLocale)
    }
  }, [changeLocaleOnLoad])

  return (
    <LocaleContext.Provider
      value={{
        ...mergedData,
        defaultLocale,
        currentLocale,
        isRTL: RTL_LANGUAGES.includes(currentLocale),
        translations,
      }}
    >
      {children}
    </LocaleContext.Provider>
  )
}

export default LocalizationProvider
